import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { Section, VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import FilterTabs from 'tio-ui/components/filter-tabs';
import SkillChip from 'tio-ui/components/skill-chip';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import {
  PhStar,
  PhHeart,
  PhShare,
  PhQuotes,
  PhTrendUp,
  PhClock,
  PhBriefcase,
  PhArrowRight,
  PhFunnel,
  PhMagnifyingGlass,
  PhSparkle,
  PhPath,
} from 'ember-phosphor-icons';
import PageHero from 'tio-ui/components/page-hero';
import type { SuccessStory } from 'tio-employee/services/career-pathways-api';

const FILTERS = [
  { id: 'all', label: 'All Stories' },
  { id: 'featured', label: 'Featured' },
  { id: 'leadership', label: 'Leadership Paths' },
  { id: 'promotion', label: 'Promotions' },
  { id: 'career-change', label: 'Career Changes' },
];

interface CareerPathwaysSuccessStoriesSignature {
  Args: {
    model: {
      stories: SuccessStory[];
    };
  };
}

export default class CareerPathwaysSuccessStories extends Component<CareerPathwaysSuccessStoriesSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;
  @tracked activeFilter = 'all';
  @tracked likedStories: string[] = [];

  get companyName() {
    if (this.reimaginedData.isDemoMode) {
      return getCurrentDemoClientData().client.name;
    }

    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get stories() {
    return this.args.model.stories;
  }

  setFilter = (filterId: string) => {
    this.activeFilter = filterId;
  };

  toggleLike = (storyId: string) => {
    if (this.likedStories.includes(storyId)) {
      this.likedStories = this.likedStories.filter((id) => id !== storyId);
    } else {
      this.likedStories = [...this.likedStories, storyId];
    }
  };

  isLiked = (storyId: string) => {
    return this.likedStories.includes(storyId);
  };

  get filteredStories() {
    if (this.activeFilter === 'featured') {
      return this.stories.filter((s) => s.featured);
    }
    return this.stories;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{! Header }}
      <PageHero @gradient="warm">
        <:default>
          <div class="tio-row-start-between">
            <div>
              <div class="tio-row mb-2">
                <PhStar @size={{32}} />
                <Heading @level={{1}} class="tio-fg-inverse">Success Stories</Heading>
              </div>
              <Text @size="lg" class="tio-on-hero-meta max-w-2xl">
                Real employees who transformed their careers using Career Pathways and Tuition
                Assistance. Get inspired by their journeys!
              </Text>
            </div>
            <div class="tio-on-hero-tile rounded-lg p-4 text-center">
              <div class="tio-text-4xl-bold">847</div>
              <Text class="tio-on-hero-meta">Career Transitions</Text>
              <Text @size="xs" class="tio-on-hero-meta">at {{this.companyName}}</Text>
            </div>
          </div>
        </:default>
        <:footer>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div class="tio-on-hero-tile rounded-lg p-3 text-center">
              <div class="tio-text-2xl-bold">+38%</div>
              <Text @size="sm" class="tio-on-hero-meta">Avg Salary Increase</Text>
            </div>
            <div class="tio-on-hero-tile rounded-lg p-3 text-center">
              <div class="tio-text-2xl-bold">18mo</div>
              <Text @size="sm" class="tio-on-hero-meta">Avg Transition Time</Text>
            </div>
            <div class="tio-on-hero-tile rounded-lg p-3 text-center">
              <div class="tio-text-2xl-bold">92%</div>
              <Text @size="sm" class="tio-on-hero-meta">Still in New Role</Text>
            </div>
            <div class="tio-on-hero-tile rounded-lg p-3 text-center">
              <div class="tio-text-2xl-bold">4.8/5</div>
              <Text @size="sm" class="tio-on-hero-meta">Satisfaction Rating</Text>
            </div>
          </div>
        </:footer>
      </PageHero>

      {{! Filters }}
      <div class="tio-bg-surface rounded-lg shadow-sm p-4 mb-6">
        <div class="tio-row-between">
          <FilterTabs
            @tabs={{FILTERS}}
            @activeId={{this.activeFilter}}
            @onSelect={{this.setFilter}}
            @showCounts={{false}}
            @activeClass="tio-bg-warning text-white"
            @inactiveClass="tio-bg-sunken tio-fg-secondary hover:tio-bg-sunken-hover"
          />
          <div class="tio-flex-tight">
            <Button
              @intent="default"
              @appearance="outlined"
              @size="sm"
              @icon={{PhFunnel}}
              @label="More Filters"
            />
            <div class="relative">
              <PhMagnifyingGlass
                @size={{16}}
                class="absolute left-3 top-1/2 -translate-y-1/2 tio-fg-disabled"
              />
              <input type="text" placeholder="Search stories..." class="tio-story-search" />
            </div>
          </div>
        </div>
      </div>

      {{! Stories Grid }}
      <div class="space-y-6">
        {{#each this.filteredStories as |story|}}
          <div class="tio-story-card {{if story.featured 'tio-story-card--featured'}}">
            {{#if story.featured}}
              <div class="tio-story-ribbon">
                <PhSparkle @size={{16}} />
                Featured Story
              </div>
            {{/if}}

            <div class="p-6">
              <div class="flex gap-6">
                {{! Left - Avatar and Basic Info }}
                <div class="shrink-0 text-center">
                  <div
                    class="w-20 h-20 rounded-full bg-gradient-to-br
                      {{story.avatarColor}}
                      tio-row-center text-white text-2xl font-bold mb-3"
                  >
                    {{story.initials}}
                  </div>
                  <div class="font-semibold tio-fg-primary">{{story.employeeName}}</div>
                  <Text @variant="muted" @size="sm">{{story.department}}</Text>
                </div>

                {{! Middle - Journey and Quote }}
                <div class="flex-1">
                  {{! Career Journey }}
                  <div class="tio-row mb-4">
                    <div class="text-center">
                      <Text @variant="muted" @size="xs">From</Text>
                      <div class="font-medium tio-fg-secondary">{{story.fromRole}}</div>
                    </div>
                    <div class="flex-1 tio-row-tight">
                      <div class="tio-story-journey-line"></div>
                      <PhArrowRight @size={{20}} class="tio-fg-warning" />
                      <div class="tio-story-journey-line tio-story-journey-line--end"></div>
                    </div>
                    <div class="text-center">
                      <Text @variant="muted" @size="xs">To</Text>
                      <div class="font-semibold tio-fg-success">{{story.toRole}}</div>
                    </div>
                  </div>

                  {{! Quote }}
                  <div class="tio-story-quote mb-4">
                    <PhQuotes @size={{24}} class="tio-story-quote-mark" />
                    <Text class="italic tio-fg-secondary pl-4">"{{story.quote}}"</Text>
                  </div>

                  {{! Skills and Programs }}
                  <div class="flex gap-6">
                    <div>
                      <Text @variant="muted" @size="sm" class="mb-2">Skills Developed:</Text>
                      <div class="tio-wrap-extra-tight">
                        {{#each story.skillsGained as |skill|}}
                          <SkillChip @name={{skill}} @intent="primary" @showLevel={{false}} />
                        {{/each}}
                      </div>
                    </div>
                    <div>
                      <Text @variant="muted" @size="sm" class="mb-2">Programs Completed:</Text>
                      <div class="tio-wrap-extra-tight">
                        {{#each story.programsCompleted as |program|}}
                          <SkillChip @name={{program}} @intent="success" @showLevel={{false}} />
                        {{/each}}
                      </div>
                    </div>
                  </div>
                </div>

                {{! Right - Stats }}
                <div class="shrink-0 w-32 space-y-3">
                  <div class="text-center p-3 tio-bg-success-soft rounded-lg">
                    <PhTrendUp @size={{20}} class="mx-auto tio-fg-success mb-1" />
                    <div class="font-bold tio-fg-success">{{story.salaryIncrease}}</div>
                    <Text @variant="muted" @size="xs">Salary</Text>
                  </div>
                  <div class="text-center p-3 tio-bg-info-soft rounded-lg">
                    <PhClock @size={{20}} class="mx-auto tio-fg-info mb-1" />
                    <div class="font-bold tio-fg-info">{{story.timeframe}}</div>
                    <Text @variant="muted" @size="xs">Timeline</Text>
                  </div>
                </div>
              </div>

              {{! Actions }}
              <div class="tio-row-between pt-4 mt-4 border-t tio-border-subtle">
                <div class="tio-row-loose">
                  <button
                    type="button"
                    {{on "click" (fn this.toggleLike story.id)}}
                    class="tio-story-like {{if (this.isLiked story.id) 'tio-story-like--active'}}"
                  >
                    <PhHeart @size={{20}} @weight={{if (this.isLiked story.id) "fill" "regular"}} />
                    <span>
                      {{#if (this.isLiked story.id)}}
                        {{story.likes}}
                        + 1
                      {{else}}
                        {{story.likes}}
                      {{/if}}
                    </span>
                  </button>
                  <button
                    type="button"
                    class="tio-row-tight text-sm tio-fg-muted hover:tio-fg-brand-accent"
                  >
                    <PhShare @size={{20}} />
                    <span>Share</span>
                  </button>
                </div>
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  @size="sm"
                  @icon={{PhArrowRight}}
                  @iconPlacement="end"
                  @label="View Full Journey"
                />
              </div>
            </div>
          </div>
        {{/each}}
      </div>

      {{! Load More }}
      <div class="text-center py-8">
        <Button @intent="default" @appearance="outlined">
          Load More Stories
        </Button>
        <Text @variant="muted" @size="sm" class="mt-2">
          Showing 5 of 847 success stories
        </Text>
      </div>

      {{! CTA Section }}
      <Section class="tio-story-cta">
        <:body>
          <PhStar @size={{48}} class="mx-auto mb-4 tio-fg-warning" />
          <Heading @level={{2}} class="tio-fg-inverse mb-2">
            Ready to Write Your Own Success Story?
          </Heading>
          <Text class="tio-story-cta-subtitle mb-6 max-w-xl mx-auto">
            Join hundreds of employees who have transformed their careers using Career Pathways.
            Start your journey today.
          </Text>
          <div class="tio-flex-center gap-4">
            <Button
              @intent="default"
              @icon={{PhPath}}
              @label="Start Your Plan"
              class="tio-bg-surface tio-fg-brand hover:tio-bg-sunken"
            />
            <Button
              @intent="default"
              @icon={{PhBriefcase}}
              @label="Explore Careers"
              class="tio-on-hero-tile tio-fg-inverse border-white/30"
            />
          </div>
        </:body>
      </Section>
    </VStack>
  </template>
}
